import { now, subtract } from '@eturi/date-util'
import { useT } from '@eturi/react-i18next'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import * as Yup from 'yup'
import { tError } from '../widgets'
import { useBirthdayDisplayAge } from './useBirthdayDisplayAge'

const BIRTHDAY_DEFAULT = 'N/A'
// NOTE: We aren't using a standard localized format b/c birthday isn't
//  displayed We actually only display age. The fact that we have a field
//  "birthday" that we try to calculate based on "age" is really silly.
const BIRTHDAY_FORMAT = 'YYYY-MM-DD'

const parseAgeToBirthday = (age: number) => {
	const date = subtract(now.subtract(age || 0, 'y'), 1, 'd')
	const MM = `${date.getMonth() + 1}`.padStart(2, '0')
	const DD = `${date.getDate()}`.padStart(2, '0')

	return `${date.getFullYear()}-${MM}-${DD}`
}

const parseBirthdayToAge = (birthday: Maybe<string>) =>
	birthday ? new Date().getFullYear() - moment(birthday, BIRTHDAY_FORMAT).year() : 0

/**
 * Hook to help construct a Birthday picker.
 */
export const useBirthdayPicker = (birthday: Maybe<string>) => {
	const [t] = useT()
	const displayAge = useBirthdayDisplayAge()

	return useMemo(() => {
		const initialBirthday = birthday || BIRTHDAY_DEFAULT
		const initialAge = parseBirthdayToAge(birthday)

		const birthdayOptions = Array.from({ length: 19 }, (_, age) => {
			// Make sure not to change the birthday if the age doesn't change
			const value =
				age === 0 ? BIRTHDAY_DEFAULT
				: age === initialAge ? initialBirthday
				: parseAgeToBirthday(age)

			const label = displayAge(age)

			return { label, value }
		})

		return {
			birthdayToAge: parseBirthdayToAge,
			emptyValue: BIRTHDAY_DEFAULT,
			options: birthdayOptions,
			initialValue: initialBirthday,
			validate: Yup.string().test({
				test: (v: Maybe<string>) => Boolean(v) && v !== BIRTHDAY_DEFAULT,
				message: tError(t('error_strings.field_required', { field: t('form_controls.age') })),
			}),
			serverValue: (v: string) => (v === BIRTHDAY_DEFAULT ? null : v),
		}
	}, [birthday, displayAge, t])
}
